/**
 * The request never reached a server, or its response never arrived.
 *
 * `fetch` rejects with a bare `TypeError` for DNS failure, connection refusal,
 * TLS failure, CORS rejection and an offline browser — indistinguishable, by
 * type, from a `TypeError` thrown while rendering. That ambiguity is not
 * resolvable at a catch site, so it is resolved HERE, where the code already
 * knows the throw came out of `fetch`.
 *
 * It cost a real defect: the route error boundary classified anything that was
 * not an `ApiError` as a render crash, so a cold load with no network reported
 * "this screen failed to render" and withheld the Retry button — on the one
 * failure that a retry always fits.
 *
 * `cause` keeps the original for telemetry; nothing should switch on it.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(opts: { code: string; message: string; status: number; details?: unknown }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.details = opts.details ?? null;
  }

  static async fromResponse(res: Response): Promise<ApiError> {
    let code = 'unknown';
    let message = `${res.status} ${res.statusText}`;
    let details: unknown = null;
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string; details?: unknown };
        code?: unknown;
        message?: unknown;
      };
      if (body?.error) {
        code = body.error.code ?? code;
        message = body.error.message ?? message;
        details = body.error.details ?? null;
      } else if (typeof body?.code === 'string') {
        // The flat shape, `{code, message}`: what the maintenance gate in front of the API
        // answers (batch-worker's under the `full` hold, the retired Worker's freeze build).
        code = body.code;
        if (typeof body.message === 'string') message = body.message;
      }
    } catch {
      // non-JSON body — fall through with status text
    }
    return new ApiError({ code, message, status: res.status, details });
  }
}
